import axios from 'axios';
import { 
  Base,
  CreateBaseRequest,
  UpdateBaseRequest,
  ApiResponse
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('teaam_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('teaam_token');
      localStorage.removeItem('teaam_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const baseService = {
  // Get all active bases (for dropdown)
  async getActiveBases(): Promise<Base[]> {
    try {
      const response = await apiClient.get<ApiResponse<Base[]>>('/bases');
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch bases');
      }
      
      return response.data.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to fetch active bases');
    }
  },

  // Get all bases (admin)
  async getAllBases(): Promise<Base[]> {
    try {
      const response = await apiClient.get<ApiResponse<Base[]>>('/admin/bases');
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch bases');
      }
      
      return response.data.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to fetch bases');
    }
  },

  // Get single base by ID (admin)
  async getBase(id: string): Promise<Base> {
    try {
      const response = await apiClient.get<ApiResponse<Base>>(`/admin/bases/${id}`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch base');
      }
      
      return response.data.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to fetch base details');
    }
  },

  // Create new base (admin)
  async createBase(data: CreateBaseRequest): Promise<Base> {
    try {
      const response = await apiClient.post<ApiResponse<Base>>('/admin/bases', data);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to create base');
      }
      
      return response.data.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to create base');
    }
  },

  // Update base (admin)
  async updateBase(id: string, data: UpdateBaseRequest): Promise<Base> {
    try {
      const response = await apiClient.put<ApiResponse<Base>>(`/admin/bases/${id}`, data);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to update base');
      }
      
      return response.data.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to update base');
    }
  },

  // Delete base (admin)
  async deleteBase(id: string): Promise<void> {
    try {
      const response = await apiClient.delete<ApiResponse<void>>(`/admin/bases/${id}`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to delete base');
      }
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to delete base');
    }
  },
};

export default baseService;